import { useCallback } from 'react';

import { useEntitySchema, useStack9 } from '@april9au/stack9-react';
import { ColumnConfigurationRenderAs, ep } from '@april9au/stack9-sdk';
import * as ui from '@april9au/stack9-ui';

import { entityNames } from '../../constants/entities';
import { ApplicationResponse } from '../../models/stack9/ApplicationResponse';
import { ListTable } from '../ListTable/ListTable';

const { useScreen } = ui;
const { useForm } = ui.S9Form;
const { useRefreshList } = ui;
const S9Drawer = ui.S9Drawer.default;
const S9Form = ui.S9Form.default;
const S9Fieldset = ui.S9Fieldset.default;
const S9EntityActions = ui.S9EntityActions.default;

type ResponseFormProps = {
  onClose: () => void;
  initialValues?: Partial<ApplicationResponse>;
  id: number;
};

const preprocessTextareaValueForMarkdown = (
  text: string | null | undefined,
) => {
  if (!text) return '';

  // 1. Normalize line endings (Windows -> Unix)
  let result = text.replace(/\r\n/g, '\n');
  result = result.replace(/\r/g, '\n');

  // 2. Convert newlines to markdown line breaks
  result = result.replace(/\n/g, '<br />');

  // 3. (Optional) Handle tabs - convert to spaces
  result = result.replace(/\t/g, '    ');

  // 4. Trim trailing/leading whitespace
  return result.trim();
};

export const ResponseForm = ({
  onClose,
  initialValues,
  id,
}: ResponseFormProps) => {
  const [form] = useForm();
  const { screen } = useScreen();
  const screenKey = ep(screen && screen.head.key);

  const formFields = [
    { field: 'requirement_code', colSize: 12 },
    { field: 'status', colSize: 12 },
    { field: 'response_option' },
    { field: 'response_text' },
    { field: 'assessor_comment' },
  ];

  const { queryService } = useStack9();
  const { schema, isValidating } = useEntitySchema(
    entityNames.APPLICATION_RESPONSE,
  );
  const refresh = useRefreshList();

  const handleSave = useCallback(
    async (values: ApplicationResponse) => {
      await queryService.runNamedQuery(
        screenKey,
        'updateApplicationResponseDrawer',
        {
          vars: {
            requirement_code: values.requirement_code,
            status: values.status ?? null,
            response_option: values.response_option ?? null,

            // TASK-83: Portal gets value from textarea input and has \n characters which throw error while saving due to Stack9 query adapter body parsing logic
            // Since we are rendering the values in Markdown editor anyway, we can preprocess them to be compatible
            response_text: preprocessTextareaValueForMarkdown(
              values.response_text,
            ),
            assessor_comment: values.assessor_comment ?? null,
            id,
          },
        },
      );
      onClose?.();
      refresh();
    },
    [id, onClose, queryService, refresh, screenKey],
  );

  return (
    <S9Form
      isValidating={isValidating}
      onFinish={handleSave}
      loading={isValidating}
      form={form}
      initialValues={initialValues}
    >
      <S9Drawer.Header
        title="Application response"
        showCancel
        extra={
          <S9EntityActions
            entityKey={entityNames.APPLICATION_RESPONSE}
            entityId={id}
            form={form}
          />
        }
      />
      <S9Drawer.Content>
        {schema && (
          <>
            <S9Fieldset
              context={{ id: undefined }}
              schema={schema}
              formFields={formFields}
            />
            <ListTable
              queryVars={{ applicationResponseId: id }}
              queryName="getapplicationdocumentbyapplicationresponse"
              title="Application documents"
              columns={[
                {
                  label: 'Code',
                  field: 'code',
                  value: '{{code}}',
                  renderAs: ColumnConfigurationRenderAs.Text,
                },
                {
                  label: 'Files',
                  field: 'file',
                  value: '{{file}}',
                  renderAs: ColumnConfigurationRenderAs.CustomComponent,
                  options: {
                    customComponentName: 'FileView',
                  },
                },
              ]}
            />
          </>
        )}
      </S9Drawer.Content>
    </S9Form>
  );
};
